/**
 * Голос через LiveKit (спецификация 5.4): вход даёт токен комнаты (комната = канал),
 * дальше клиент сам держит WebRTC-соединение. Состояния «кто где» приходят по шлюзу
 * в стор серверов; здесь — только своё соединение, микрофон, звук и экран.
 */
import { startVoiceActivity } from '@/lib/voiceActivity'
import { create } from 'zustand'
import { AudioPresets, DisconnectReason, Room, RoomEvent, Track, facingModeFromLocalTrack, type ConnectionQuality, type ConnectionState, type LocalTrackPublication, type Participant, type RemoteParticipant, type RemoteTrack, type RemoteTrackPublication, type VideoCodec } from 'livekit-client'
import { DEFAULT_SHARE_QUALITY, canShareScreen, clampShareQuality, isShareFps, isShareResolution, restrictOwnAudioSupported, shareAudio, shareBitrate, shareCapture, shareCaps, type ScreenShareQuality, type ShareAudio } from '@/lib/screenShare'
import { autoVideoCodec, CAMERA_ENCODING, screenEncoding } from '@/lib/videoCodec'
import { isStaging } from '@/lib/stage'
import { guildApi } from '@/lib/guildApi'
import { useAuthStore } from './auth'
import { useGuildStore } from './guilds'
import { playSound, type SoundName } from '@/lib/sounds'
import { soundUrl, soundboardApi } from '@/lib/soundboard'
import { gatewayClient } from '@/lib/gatewaySingleton'
import { describeError } from '@/lib/auth'
import { isPseudoDevice } from '@/lib/devices'
import { MicProcessor, type InputMode } from '@/lib/audio/micProcessor'
import { createDucker, type Ducker } from '@/lib/audio/duck'
import { StreamStripProcessor, type StripState } from '@/lib/audio/streamStrip'
import { startSystemAudio, type SystemAudioCapture, type SystemAudioStats } from '@/lib/audio/systemAudio'
import { describeMediaError, isBusyDevice, isDenied, isMissingDevice, mediaErrorHint } from '@/lib/mediaErrors'
import { listenPTT, pttMouseButton, typingInInput } from '@/lib/audio/ptt'
import { desktop, desktopPlatform } from '@/lib/desktop'
import { selfStateAction } from '@/lib/voiceSelf'
import type { SoundboardSound, VoiceChannelEffect, VoiceJoin, VoiceState } from '@/types/api'

export type VoiceStatus = 'connecting' | 'connected' | 'reconnecting'

export interface VoiceConnection {
  /** null — звонок в ЛС или группе. */
  guildId: string | null
  channelId: string
  status: VoiceStatus
}

/** Настройки «Голос и видео»: устройства, обработка звука и режим активации; хранятся в этом браузере. */
export interface VoicePrefs {
  audioinput: string
  audiooutput: string
  videoinput: string
  startMuted: boolean
  noiseSuppression: boolean
  echoCancellation: boolean
  autoGainControl: boolean
  /** По голосу или push-to-talk. */
  inputMode: InputMode
  /** Клавиша PTT (KeyboardEvent.code) или Mouse3/Mouse4/Mouse5. */
  pttKey: string
  /** Порог активации по голосу: автоматически или vadThreshold в дБ (−100…0). */
  vadAuto: boolean
  vadThreshold: number
  /** Шумоподавление RNNoise (WebAssembly) поверх браузерного. */
  rnnoise: boolean
  /** Качество демонстрации экрана, выбранное в последний раз. */
  screenShare: ScreenShareQuality
  /** Общая громкость своего микрофона (1 = как есть, до 2). */
  inputVolume: number
  /** Общая громкость входящего звука: множитель поверх личных громкостей. */
  outputVolume: number
  /** Личная громкость собеседников по id (1 = 100%, до 2). */
  userVolumes: Record<string, number>
  /** Громкость звука их трансляций — отдельной ручкой: эхо чаще всего именно оттуда. */
  streamVolumes: Record<string, number>
  /** Приглушать звук чужой трансляции, пока говорю: в него попадает мой же голос и возвращается эхом. */
  duckStream: boolean
  /** Версия сохранённых настроек: по ней разово переносим значения при изменении умолчаний. */
  prefsVersion: number
  /** Вычитать голоса собеседников из звука своей трансляции (Windows: захват отдаёт весь микс системы). */
  stripVoices: boolean
  /** Звуки событий: подключение и выход, микрофон, демонстрация экрана, камера. */
  sounds: boolean
  /** Громкость звуковой панели, 0…1: множитель к громкости самого звука. */
  soundboardVolume: number
}

const PREFS_KEY = 'klichat.voice'
/** Клавиша рации по умолчанию; «Сбросить» в горячих клавишах возвращает её. */
export const DEFAULT_PTT_KEY = 'Backquote'
const defaultPrefs: VoicePrefs = {
  audioinput: '', audiooutput: '', videoinput: '', startMuted: false, noiseSuppression: true, echoCancellation: true, autoGainControl: true,
  inputMode: 'voice', pttKey: DEFAULT_PTT_KEY, vadAuto: true, vadThreshold: -60, rnnoise: true,
  screenShare: DEFAULT_SHARE_QUALITY,
  inputVolume: 1,
  outputVolume: 1,
  userVolumes: {},
  streamVolumes: {},
  duckStream: false,
  prefsVersion: 1,
  stripVoices: true,
  sounds: true,
  soundboardVolume: 0.7,
}

function loadPrefs(): VoicePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    if (!raw) return defaultPrefs
    const p = { ...defaultPrefs, ...(JSON.parse(raw) as Partial<VoicePrefs>) }
    const q = p.screenShare as Partial<ScreenShareQuality> | undefined
    // раньше звук хранился полем audio с обратным смыслом: у старых настроек его просто нет,
    // и человек получает звук — как и ждёт тот, кто показывает игру
    // «как на экране» убрали — такой выбор больше не проходит проверку, но звук не переспрашиваем
    p.screenShare = q && isShareResolution(q.resolution) && isShareFps(q.fps) ? { resolution: q.resolution, fps: q.fps, muteAudio: q.muteAudio === true } : { ...DEFAULT_SHARE_QUALITY, muteAudio: q?.muteAudio === true }
    /*
     * 26.09.2026 шли тестовые профили: RNNoise 2024 (rnnoiseNew) и DeepFilterNet (deepfilter). Остался
     * обычный RNNoise. Профиль DeepFilterNet выключал RNNoise и встроенное шумоподавление — без него
     * у выбравших их остался бы голый микрофон, поэтому возвращаем RNNoise. Ключи убираем.
     */
    const legacy = p as VoicePrefs & { deepfilter?: boolean; rnnoiseNew?: boolean }
    if (legacy.deepfilter) {
      p.rnnoise = true
      p.noiseSuppression = true
    }
    delete legacy.deepfilter
    delete legacy.rnnoiseNew
    /*
     * 26.09.2026 убран и профиль «Встроенное шумоподавление»: остались RNNoise (поверх встроенного — так его
     * и выбрали) и «Без обработки». Встроенное теперь следует за RNNoise; кто сидел на одном встроенном,
     * получает RNNoise.
     */
    if (!p.rnnoise && p.noiseSuppression) p.rnnoise = true
    p.noiseSuppression = p.rnnoise
    // служебные устройства Chrome («Default - …», «Communications - …») больше не выбрать — это «По умолчанию»
    if (isPseudoDevice(p.audioinput)) p.audioinput = ''
    if (isPseudoDevice(p.audiooutput)) p.audiooutput = ''
    /*
     * Приглушение чужой трансляции, пока говоришь, было включено по умолчанию: в звук
     * трансляции попадал голос слушателя и возвращался к нему эхом. Захват без звука
     * самого Кличата и вычитание голосов решают это у того, кто показывает, — а здесь
     * оставалось только «звук просел, когда я заговорил». Выключаем один раз и у тех,
     * у кого настройка уже сохранена; вернуть можно в настройках голоса.
     */
    if (!p.prefsVersion) {
      p.duckStream = false
      p.prefsVersion = 1
    }
    p.inputVolume = clampVolume(p.inputVolume)
    p.outputVolume = clampVolume(p.outputVolume)
    p.soundboardVolume = Math.min(1, clampVolume(p.soundboardVolume ?? defaultPrefs.soundboardVolume))
    if (!p.userVolumes || typeof p.userVolumes !== 'object') p.userVolumes = {}
    if (!p.streamVolumes || typeof p.streamVolumes !== 'object') p.streamVolumes = {}
    return p
  } catch {
    return defaultPrefs
  }
}

function savePrefs(p: VoicePrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p))
  } catch {
    /* приватный режим */
  }
}

interface VoiceStore {
  afk: boolean
  connection: VoiceConnection | null
  selfMute: boolean
  selfDeaf: boolean
  sharing: boolean
  camera: boolean
  prefs: VoicePrefs
  /** Кто говорит (по событиям LiveKit, без сервера). */
  speaking: Record<string, boolean>
  /** У кого в трансляции есть звуковая дорожка: без неё тишина — это выбор показывающего, а не наша поломка. */
  streamAudio: Record<string, boolean>
  /** Вычитание голосов из своей трансляции: null — не работает, иначе насколько тише стали голоса. */
  strip: StripState | null
  /** Звук трансляции приходит от системы уже без нашего — вычитать нечего. null, когда идём старым путём. */
  cleanAudio: SystemAudioStats | null
  /** Браузер принял restrictOwnAudio: звука вкладки Кличата (голосов) в захвате нет, фильтр не нужен. */
  ownAudioRestricted: boolean
  /** Треки демонстрации экрана и камер по id пользователя. Чужой экран здесь, только пока его смотрят. */
  screens: Record<string, Track>
  cameras: Record<string, Track>
  /**
   * Чьи трансляции смотрим. На чужой экран не подписываемся, пока не нажали «Смотреть стрим»:
   * несколько мегабит на каждого зрителя не должны приезжать к тому, кто на экран не смотрит.
   * В звонке ЛС плиток нет — там трансляция открывается сразу, как и раньше.
   */
  watching: Record<string, true>
  /**
   * Кто смотрит чьи трансляции: id показывающего → id зрителей. Сервер этого не знает: смотреть — значит
   * подписаться на дорожку в LiveKit. Поэтому каждый зритель сам говорит комнате, что смотрит (данные
   * LiveKit с темой klichat.watch; отправителя подставляет LiveKit — за другого не скажешь), себя считаем сами.
   */
  viewers: Record<string, string[]>
  /** Кто показывает экран в нашей комнате (по публикациям LiveKit): id → название окна или ''. */
  live: Record<string, string>
  /** Чей экран развёрнут в комнате канала; null — плитки. */
  focus: string | null
  /** Качество связи по участникам (свой id — собственное). */
  quality: Record<string, ConnectionQuality>
  /** Пинг до голосового сервера (сигналинг LiveKit), мс. */
  rtt: number | null
  /** Кто сейчас с нами в комнате LiveKit (id участника → true). */
  remote: Record<string, true>
  error: string | null
  micError: string | null
  /** Что ещё проверить на этой системе: у Windows свои разрешения на микрофон. */
  micHint: string | null
  /** К чему относится подсказка: от этого зависит, какую страницу настроек открывать. */
  hintKind: 'mic' | 'camera' | null
  /** Ворота микрофона открыты: голос уходит в SFU (по голосу или нажатой клавише PTT). */
  transmitting: boolean
  /** RNNoise действительно запустился в worklet'е; null — пока не подключались к голосу. */
  rnnoise: boolean | null
  /** Кто только что сыграл звук панели: id → эмодзи звука; держится несколько секунд для отметки у имени. */
  effects: Record<string, VoiceEffectMark>

  /** Вход в голосовой канал сервера или в звонок ЛС (guildId null). */
  join: (guildId: string | null, channelId: string) => Promise<void>
  leave: () => Promise<void>
  toggleMute: () => Promise<void>
  toggleDeaf: () => Promise<void>
  toggleScreen: () => Promise<void>
  /**
   * Начать демонстрацию с выбранным качеством (запоминается в prefs). title — название окна
   * из выбора в приложении: его видят в комнате на плитке трансляции, пока её не открыли.
   */
  startScreen: (quality: ScreenShareQuality, title?: string, testCodec?: VideoCodec) => Promise<void>
  stopScreen: () => Promise<void>
  /** Ещё раз включить микрофон после ошибки: устройство освободилось — перезаходить в канал незачем. */
  retryMic: () => Promise<void>
  /** Личная громкость собеседника: 0…2 (1 = 100%), запоминается. */
  setUserVolume: (userId: string, volume: number) => void
  /** Громкость звука чужой трансляции (0…2): эхо снимается, не трогая голос. */
  setStreamVolume: (userId: string, volume: number) => void
  /** Выключить звук трансляции или вернуть прежнюю громкость — одной кнопкой, в том числе с телефона. */
  toggleStreamMute: (userId: string) => void
  /** Смотреть трансляцию: подписаться на экран и его звук и развернуть её в комнате. */
  watchStream: (userId: string) => void
  /** Закрыть просмотр: отписаться, чтобы поток не шёл зря. */
  stopWatching: (userId: string) => void
  /** Развернуть трансляцию в комнате или вернуться к плиткам (null), не отписываясь. */
  setFocus: (userId: string | null) => void
  toggleCamera: () => Promise<void>
  /** Передняя камера ↔ задняя (телефон, планшет); где сторон у камер нет — следующая по списку. */
  flipCamera: () => Promise<void>
  setPrefs: (patch: Partial<VoicePrefs>) => Promise<void>
  applyEvent: (t: string, d: unknown) => void
  /** Сыграть звук панели в канале, где сейчас сам: услышат все в канале (VOICE_CHANNEL_EFFECT_SEND). */
  sendSound: (sound: SoundboardSound) => Promise<void>
  /** id текущего пользователя — чтобы отличать свои события. */
  setSelf: (userId: string | null) => void
}

let room: Room | null = null
/*
 * restrictOwnAudio — пока только на тестовом сервере. Неизвестно, убирает ли Chrome из захвата
 * звука системы одну нашу вкладку или весь звук браузера: во втором случае пропал бы YouTube из
 * соседней вкладки, ради которого трансляцию часто и включают. Проверили на dev (Windows, Chrome,
 * YouTube в другой вкладке слышно, себя зрители не слышат) — включить везде.
 */
const RESTRICT_OWN_AUDIO = isStaging
/** Канал, к комнате которого подключён room: по нему видно, что VOICE_SERVER_UPDATE зовёт в другую. */
let roomChannel: string | null = null
/** Сессия шлюза из ответа на вход: по ней видно, что голос ушёл на другое устройство. */
let joinedSession: string | null = null
/** Номер последнего подключения: старое, которое обогнал новый перенос, ничего не трогает. */
let connectGen = 0
/*
 * Сервер вывел нас из комнаты, а куда — ещё неизвестно: перенос или отключение. Решение придёт
 * по шлюзу (VOICE_SERVER_UPDATE или VOICE_STATE_UPDATE без канала); не пришло за это время —
 * считаем, что отключили.
 */
let removedWait: ReturnType<typeof setTimeout> | null = null
const REMOVED_WAIT_MS = 5000
let stopActivity: (() => void) | null = null
let rttTimer: ReturnType<typeof setInterval> | null = null
let selfId: string | null = null

/** Что смотрят остальные в комнате: их id → id показывающих (из сообщений klichat.watch). */
const watchersBy = new Map<string, string[]>()
const WATCH_TOPIC = 'klichat.watch'

/** Собрать «кто смотрит» из сообщений комнаты и своего списка просмотра. */
function recomputeViewers() {
  const out: Record<string, string[]> = {}
  const add = (streamer: string, viewer: string) => {
    if (streamer !== viewer) (out[streamer] ??= []).push(viewer)
  }
  for (const [viewer, list] of watchersBy) for (const streamer of list) add(streamer, viewer)
  if (selfId) for (const streamer of Object.keys(useVoiceStore.getState().watching)) add(streamer, selfId)
  useVoiceStore.setState({ viewers: out })
}

/** Сказать комнате (или только новичкам — to), чьи трансляции смотрим сейчас. */
function sendWatching(to?: string[]) {
  const r = room
  if (!r || r.state !== 'connected') return
  const payload = new TextEncoder().encode(JSON.stringify({ watching: Object.keys(useVoiceStore.getState().watching) }))
  void r.localParticipant.publishData(payload, { reliable: true, topic: WATCH_TOPIC, ...(to ? { destinationIdentities: to } : {}) }).catch(() => undefined)
}
/*
 * Живы ли собственные ворота микрофона. Пока живы, свой огонёк «говорит» ведём от них,
 * а не от ответа сервера: сервер считает громкость окном в 400 мс, сглаживает по двум
 * окнам и присылает результат отдельным сообщением, — то есть свой же голос возвращается
 * к нам с задержкой в полсекунды и больше. Ворота знают ровно то, что уходит собеседникам,
 * и знают это сразу: закрыты — значит не слышно никому, что бы ни думал сервер.
 */
let gateLive = false
/** Настройки захвата менялись, пока микрофон был выключен: включение их само не применит. */
let captureStale = false
let moving = false
/** Был ли микрофон выключен до «не слышу» — чтобы вернуть его как было. */
let muteBeforeDeaf = false
/*
 * Какую трансляцию человека мы смотрели (sid публикации). После нашего переподключения LiveKit
 * присылает ту же публикацию заново — её открываем снова. Новый показ того же человека — это
 * новая трансляция, и сама она не открывается.
 */
const watchedSid = new Map<string, string>()
/** Громкость трансляции до «выключить звук»: кнопка возвращает ту, что была, а не 100%. */
const volumeBeforeMute = new Map<string, number>()
let mic: MicProcessor | null = null
// один звуковой контекст на всё: воспроизведение собеседников, обработка микрофона и вычитание
// их голосов из трансляции. Общий он потому, что опора для вычитания должна идти по тем же часам,
// что и захват, — иначе сигналы разъезжаются.
let audioCtx: AudioContext | null = null
/** Узел, в который стекается весь звук собеседников ровно с той громкостью, с какой он звучит. */
let refBus: GainNode | null = null
/** Копии громкостей для опоры: LiveKit крутит свою, мы повторяем её у себя. */
const refGains = new Map<string, GainNode>()

function audioContext(): AudioContext {
  audioCtx ??= new AudioContext({ sampleRate: 48000 })
  if (audioCtx.state === 'suspended') void audioCtx.resume().catch(() => undefined)
  return audioCtx
}

function referenceBus(): GainNode {
  const ctx = audioContext()
  if (!refBus || refBus.context !== ctx) {
    refBus = ctx.createGain()
    // опора моно: голоса собеседников всё равно приходят моно, а фильтру так вдвое дешевле
    refBus.channelCount = 1
    refBus.channelCountMode = 'explicit'
    refBus.channelInterpretation = 'speakers'
  }
  return refBus
}
let stopPTT: (() => void) | null = null
let sysAudio: SystemAudioCapture | null = null

function audioRoot(): HTMLElement {
  let el = document.getElementById('voice-audio')
  if (!el) {
    el = document.createElement('div')
    el.id = 'voice-audio'
    el.style.display = 'none'
    document.body.appendChild(el)
  }
  return el
}

/** Ключ копии громкости: у человека может звучать и голос, и звук его трансляции. */
const refKey = (identity: string, source: Track.Source) => `${identity}:${source}`

/**
 * Выставить громкость собеседника и повторить её в опоре для вычитания. Опора обязана совпадать
 * с тем, что слышно на самом деле: иначе из захвата вычтется не то, что в нём звучит.
 */
function applyRemoteVolume(p: RemoteParticipant, volume: number, source: Track.Source): void {
  p.setVolume(volume, source as Track.Source.Microphone | Track.Source.ScreenShareAudio)
  const g = refGains.get(refKey(p.identity, source))
  if (g) g.gain.setTargetAtTime(volume, g.context.currentTime, 0.02)
}

/** Завести отвод звука собеседника в опору: слышимое на выходе = то, что вычитаем. */
function tapReference(track: RemoteTrack, participant: RemoteParticipant, source: Track.Source): void {
  if (!('setWebAudioPlugins' in track) || typeof track.setWebAudioPlugins !== 'function') return
  const ctx = audioContext()
  const tap = ctx.createGain()
  const mirror = ctx.createGain()
  mirror.gain.value = 0
  tap.connect(mirror)
  mirror.connect(referenceBus())
  track.setWebAudioPlugins([tap])
  dropReference(participant.identity, source)
  refGains.set(refKey(participant.identity, source), mirror)
}

function dropReference(identity: string, source: Track.Source): void {
  const g = refGains.get(refKey(identity, source))
  if (!g) return
  g.disconnect()
  refGains.delete(refKey(identity, source))
}

/** Громкость собеседника с учётом «не слышу»: 0 при оглушении, иначе личная (1 = 100%, до 2). */
/** Громкость из настроек: чужое или испорченное значение не должно оглушить. */
function clampVolume(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(2, v)) : 1
}

function volumeFor(prefs: VoicePrefs, deaf: boolean, userId: string): number {
  return deaf ? 0 : (prefs.userVolumes[userId] ?? 1) * prefs.outputVolume
}

/** Громкость звука его трансляции — своя ручка, чтобы приглушить эхо, не трогая голос. */
function streamVolumeFor(prefs: VoicePrefs, deaf: boolean, userId: string): number {
  return deaf ? 0 : (prefs.streamVolumes[userId] ?? 1) * prefs.outputVolume
}

// приглушение звука трансляции, пока говорю (см. lib/audio/duck.ts)
let ducker: Ducker | null = null
/** Почему у трансляции нет звука — по-разному на разных системах. */
function noStreamAudioHint(): string {
  if (desktopPlatform === 'win32') return 'Звук системы не захватился. Проверьте, что в Windows выбрано устройство вывода, и запустите демонстрацию заново'
  if (desktopPlatform === 'darwin') return 'Система не дала звук: нужна macOS 13 или новее и доступ к записи экрана. Показываем без звука'
  if (desktopPlatform !== '') return 'Эта система не отдаёт приложениям звук других программ — показываем без звука'
  return 'Показываем без звука: браузер отдаёт звук, только если в его окне выбора отметить «Поделиться звуком». Запустите демонстрацию заново и поставьте эту галочку'
}

const duckLevel = (prefs: VoicePrefs) => (prefs.duckStream && ducker ? ducker.level() : 1)

/** Применить громкости всем в комнате: отдельно голос, отдельно звук демонстрации. */
function applyVolumes(r: Room | null, prefs: VoicePrefs, deaf: boolean): void {
  deaf = deaf || useVoiceStore.getState().afk
  if (!r) return
  const duck = duckLevel(prefs)
  for (const p of r.remoteParticipants.values()) {
    applyRemoteVolume(p, volumeFor(prefs, deaf, p.identity), Track.Source.Microphone)
    applyRemoteVolume(p, streamVolumeFor(prefs, deaf, p.identity) * duck, Track.Source.ScreenShareAudio)
  }
}

/** Отметка у имени того, кто сыграл звук панели: эмодзи звука и когда. */
export interface VoiceEffectMark {
  emoji: VoiceChannelEffect['emoji']
  /** Растёт с каждым звуком — по нему отметка перезапускает анимацию. */
  key: number
}

let effectKey = 0
const effectTimers = new Map<string, ReturnType<typeof setTimeout>>()

/** Звуки панели: декодированные буферы по адресу — повтор того же звука не качается заново. */
const boardBuffers = new Map<string, Promise<AudioBuffer>>()

function boardBuffer(url: string): Promise<AudioBuffer> {
  let p = boardBuffers.get(url)
  if (!p) {
    p = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`звук ${r.status}`)
        return r.arrayBuffer()
      })
      .then((b) => audioContext().decodeAudioData(b))
    p.catch(() => boardBuffers.delete(url))
    boardBuffers.set(url, p)
  }
  return p
}

/**
 * Сыграть звук панели через общий граф Web Audio: туда же и на то же устройство, что и голоса.
 * Копия уходит в опору вычитания — иначе звук попал бы в системный звук своей трансляции, и
 * зрители (они и так слышат его сами) услышали бы его дважды.
 */
async function playBoardSound(url: string, volume: number): Promise<void> {
  try {
    const ctx = audioContext()
    const buffer = await boardBuffer(url)
    const src = ctx.createBufferSource()
    src.buffer = buffer
    const gain = ctx.createGain()
    gain.gain.value = volume
    src.connect(gain)
    gain.connect(ctx.destination)
    gain.connect(referenceBus())
    src.onended = () => gain.disconnect()
    src.start()
  } catch {
    /* файл не скачался или не разобрался — без звука */
  }
}

/** Оглушён ли человек — сам или сервером: голосов он не слышит. */
function deafNow(): boolean {
  const s = useVoiceStore.getState()
  const cur = s.connection
  const serverDeaf = cur?.guildId && selfId ? useGuildStore.getState().voiceStates[cur.guildId]?.[selfId]?.deaf : false
  return s.afk || s.selfDeaf || Boolean(serverDeaf)
}

/**
 * Кто говорит — для зелёных рамок. Оглушённый чужих не слышит — и рамок у них не видит, как
 * в Discord: иначе по рамкам было видно, кто говорит, хотя сам звук выключен. Про себя знаем
 * по своим воротам точнее сервера.
 */
function visibleSpeakers(speakers: Participant[], transmitting: boolean): Record<string, boolean> {
  const next: Record<string, boolean> = deafNow() ? {} : Object.fromEntries(speakers.map((p) => [p.identity, true]))
  if (selfId && gateLive) next[selfId] = transmitting
  return next
}

export const useVoiceStore = create<VoiceStore>((set, get) => {
  const micConfig = (p: VoicePrefs) => ({ mode: p.inputMode, auto: p.vadAuto, threshold: p.vadThreshold, rnnoise: p.rnnoise, volume: p.inputVolume })
  let strip: StreamStripProcessor | null = null
  ducker ??= createDucker(() => applyVolumes(room, get().prefs, get().selfDeaf))

  // звуки событий — по настройке и в выбранное устройство вывода
  const sfx = (name: SoundName) => {
    const p = get().prefs
    if (p.sounds) void playSound(name, { sinkId: p.audiooutput || undefined })
  }

  // отметка «сыграл звук» у имени держится три секунды; новый звук того же человека её продлевает
  const markEffect = (ev: VoiceChannelEffect) => {
    const key = ++effectKey
    set((st) => ({ effects: { ...st.effects, [ev.user_id]: { emoji: ev.emoji, key } } }))
    const prev = effectTimers.get(ev.user_id)
    if (prev) clearTimeout(prev)
    effectTimers.set(ev.user_id, setTimeout(() => {
      effectTimers.delete(ev.user_id)
      set((st) => {
        if (st.effects[ev.user_id]?.key !== key) return {}
        const next = { ...st.effects }
        delete next[ev.user_id]
        return { effects: next }
      })
    }, 3000))
  }

  // клавиша PTT слушается только в режиме push-to-talk и только пока подключены
  const syncPTT = () => {
    stopPTT?.()
    stopPTT = null
    const p = get().prefs
    const active = Boolean(room) && p.inputMode === 'ptt' && Boolean(p.pttKey)
    // в десктопе клавишу ловит оболочка — она работает и когда окно свёрнуто или идёт игра
    const shell = desktop
    if (shell) {
      if (!active) {
        void shell.setPTTKey('')
        return
      }
      const mouse = pttMouseButton(p.pttKey) >= 0
      const off = shell.onPTT((down) => {
        // набор текста мешает только клавише: кнопкой мыши в поле не печатают
        if (down && !mouse && typingInInput()) return
        mic?.setPTT(down)
      })
      let local: (() => void) | null = null
      let stopped = false
      // перехвата нет или оболочка старая и кнопку не знает (среднюю — после 0.1.21): тогда хотя бы в окне
      void shell
        .setPTTKey(p.pttKey)
        .then((ok) => {
          if (!ok && !stopped) local = listenPTT(p.pttKey, (down) => mic?.setPTT(down))
        })
        .catch(() => undefined)
      stopPTT = () => {
        stopped = true
        off()
        local?.()
        void shell.setPTTKey('')
        mic?.setPTT(false)
      }
      return
    }
    if (active) stopPTT = listenPTT(p.pttKey, (down) => mic?.setPTT(down))
  }

  /**
   * Включить микрофон. Если сохранённого устройства больше нет (сменили гарнитуру,
   * система переназначила идентификатор) — забываем его и берём основное: иначе вход
   * в канал падает навсегда, а человек видит невнятное «устройство недоступно».
   */
  const enableMic = async (r: Room): Promise<void> => {
    if (get().afk) return
    try {
      await r.localParticipant.setMicrophoneEnabled(true)
    } catch (err) {
      const p = get().prefs
      /*
       * Не открылся выбранный микрофон — пробуем тот, что система считает основным.
       *
       * Раньше так поступали только когда устройство исчезло. Но оно может и остаться
       * в списке, и всё равно не открыться: отключённая гарнитура, виртуальное устройство
       * от давно удалённой программы, занятый эксклюзивно вход. Снаружи это выглядело как
       * «микрофон занят», хотя в браузере на той же машине он спокойно работал — потому
       * что браузер берёт устройство по умолчанию, а мы шли к выбранному.
       */
      const missing = isMissingDevice(err)
      if ((!missing && !isBusyDevice(err)) || !p.audioinput) throw err
      // пропавшее устройство забываем, а неоткрывшееся оставляем в настройках:
      // сегодня гарнитура выключена, завтра включена, и выбор должен сохраниться
      const prefs = missing ? { ...p, audioinput: '' } : p
      if (missing) {
        savePrefs(prefs)
        set({ prefs })
      }
      // у комнаты свои умолчания захвата: без этого следующее включение снова пойдёт к тому же
      const capture = { ...(r.options.audioCaptureDefaults ?? {}) }
      delete capture.deviceId
      r.options.audioCaptureDefaults = capture
      await r.localParticipant.setMicrophoneEnabled(true, {
        noiseSuppression: prefs.noiseSuppression,
        echoCancellation: prefs.echoCancellation,
        autoGainControl: prefs.autoGainControl,
      })
      if (!missing) {
        set({ micHint: 'Выбранный микрофон не открылся — говорим через тот, что система считает основным. Поменять можно в «Голос и видео»', hintKind: 'mic' })
      }
    }
    // включение не создаёт дорожку заново, а снимает с неё немоту: если обработку меняли
    // с выключенным микрофоном, применить её надо отдельно
    if (captureStale) await restartMic(r)
  }

  const micFailed = (err: unknown) => set({ micError: describeMediaError(err, 'mic', desktopPlatform), micHint: mediaErrorHint(err, 'mic', desktopPlatform), hintKind: 'mic' })

  // дорожка микрофона идёт через worklet (RNNoise, ворота), в SFU уходит обработанная
  const attachMic = async (r: Room) => {
    if (get().afk) return
    const track = r.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack
    if (!track || track.getProcessor()?.name === 'klichat-mic') return
    try {
      const ctx = audioContext()
      mic ??= new MicProcessor(micConfig(get().prefs))
      mic.onGate = (open) => {
        if (get().afk) open = false
        set((s) => ({ transmitting: open, ...(selfId ? { speaking: { ...s.speaking, [selfId]: open } } : {}) }))
        ducker?.gate(open)
      }
      mic.onError = (message) => set({ error: message })
      mic.onRnnoise = (on) => set({ rnnoise: on })
      track.setAudioContext(ctx)
      await track.setProcessor(mic)
      gateLive = true
      set({ transmitting: false })
      syncPTT()
    } catch (err) {
      set({ error: err instanceof Error ? `Обработка микрофона недоступна: ${err.message}` : 'Обработка микрофона недоступна' })
    }
  }

  /*
   * Браузерное шумоподавление, эхоподавление и автоусиление задаются при захвате дорожки,
   * поменять их на лету нельзя — дорожку надо взять заново. Раньше это откладывалось
   * до следующего входа в канал, и переключатель выглядел сломанным.
   */
  const restartMic = async (r: Room): Promise<void> => {
    const p = get().prefs
    const capture = {
      noiseSuppression: p.noiseSuppression,
      echoCancellation: p.echoCancellation,
      autoGainControl: p.autoGainControl,
      ...(p.audioinput ? { deviceId: p.audioinput } : {}),
    }
    // умолчания комнаты — чтобы следующее включение микрофона взяло уже новые
    r.options.audioCaptureDefaults = { ...(r.options.audioCaptureDefaults ?? {}), ...capture }
    /*
     * Именно restartTrack, а не выключить-включить: выключение микрофона у LiveKit по
     * умолчанию дорожку не отпускает, а только глушит (stopMicTrackOnMute: false).
     * Включение возвращало ту же самую дорожку с прежними ограничениями, и смена профиля
     * не давала ничего до перезахода в канал. restartTrack берёт поток заново и сам
     * перезапускает наш обработчик на новой дорожке.
     */
    const track = r.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack
    if (!track) return
    try {
      await track.restartTrack(capture)
      captureStale = false
    } catch (err) {
      micFailed(err)
    }
  }

  /**
   * Повесить на дорожку звука трансляции вычитание голосов собеседников. Опора — тот же микс,
   * который слышит показывающий, поэтому вычитать можно точно, а не на слух.
   */
  /**
   * Применил ли браузер restrictOwnAudio к звуку трансляции: он мог принять ограничение и молча
   * не выполнить. Заодно проверяем, что обработка речи осталась выключенной, — звук трансляции
   * это музыка и игры, эхоподавление и шумодав их портят.
   */
  const ownAudioRestricted = async (): Promise<boolean> => {
    const track = room?.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio)?.audioTrack?.mediaStreamTrack
    if (!track) return false
    const settings = track.getSettings() as MediaTrackSettings & { restrictOwnAudio?: boolean }
    if (settings.echoCancellation || settings.noiseSuppression || settings.autoGainControl) {
      // applyConstraints заменяет все ограничения разом — restrictOwnAudio передаём заново
      const again = { restrictOwnAudio: true, echoCancellation: false, noiseSuppression: false, autoGainControl: false }
      await track.applyConstraints(again as MediaTrackConstraints).catch(() => undefined)
    }
    return (track.getSettings() as MediaTrackSettings & { restrictOwnAudio?: boolean }).restrictOwnAudio === true
  }

  const attachStrip = async (): Promise<void> => {
    const pub = room?.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio)
    const track = pub?.audioTrack
    if (!track || !get().prefs.stripVoices) return
    try {
      strip = new StreamStripProcessor(referenceBus(), true)
      strip.onState = (state) => set({ strip: state })
      track.setAudioContext(audioContext())
      await track.setProcessor(strip)
    } catch (err) {
      // не получилось — трансляция должна продолжаться со звуком как есть
      strip = null
      set({ strip: null })
      if (err instanceof Error) console.warn('вычитание голосов не включилось:', err.message)
    }
  }

  /**
   * Опубликовать чистый звук компьютера отдельной дорожкой. LiveKit кладёт её в один поток
   * с картинкой по source, так что рассинхрона со звуком быть не должно.
   */
  const publishSystemAudio = async (capture: SystemAudioCapture): Promise<void> => {
    await room!.localParticipant.publishTrack(capture.track, {
      source: Track.Source.ScreenShareAudio,
      // это музыка и игры, а не речь: узкий пресет речи испортил бы всё
      audioPreset: AudioPresets.musicHighQualityStereo,
      forceStereo: true,
      dtx: false,
      red: false,
    })
  }

  /** Снять и остановить чистый захват: и при остановке трансляции, и при любой ошибке по пути. */
  const dropSystemAudio = async (): Promise<void> => {
    const capture = sysAudio
    sysAudio = null
    set({ cleanAudio: null })
    if (!capture) return
    try {
      await room?.localParticipant.unpublishTrack(capture.track)
    } catch {
      /* могли ещё не успеть опубликовать */
    }
    await capture.stop()
  }

  /*
   * Прижимаем картинку к выбранному размеру.
   *
   * Запрошенный размер LiveKit отдаёт браузеру как ideal, а это пожелание: с монитора
   * крупнее выбранного кадры могут приехать родного размера. На провод они всё равно
   * не пролезут — битрейт держит потолок, — но кодировщик будет жевать лишние пиксели,
   * и картинка от этого станет только хуже. Просим ещё раз, уже через max.
   *
   * Не вышло — молчим: это ровно то, как демонстрация работала раньше, и ронять из-за
   * этого уже идущую картинку незачем.
   */
  const capScreenSize = async (quality: ScreenShareQuality): Promise<void> => {
    const track = room?.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track?.mediaStreamTrack
    if (!track) return
    const want = shareCapture(quality)
    const got = track.getSettings()
    if ((got.width ?? 0) <= want.width && (got.height ?? 0) <= want.height) return
    try {
      await track.applyConstraints({ width: { max: want.width }, height: { max: want.height } })
    } catch (err) {
      if (err instanceof Error) console.warn('размер трансляции не прижался:', err.message)
    }
  }

  /*
   * Подписки ведём сами (autoSubscribe: false при входе): голос, камеры и музыку бота берём сразу,
   * а экран и его звук — только у тех, чью трансляцию открыли. С автоподпиской сервер сам слал
   * каждому в канале полный поток демонстрации, и она разворачивалась у всех без спроса.
   */
  const isScreenSource = (source: Track.Source) => source === Track.Source.ScreenShare || source === Track.Source.ScreenShareAudio

  const syncSubscription = (pub: RemoteTrackPublication, p: RemoteParticipant) => {
    const want = !get().afk && (!isScreenSource(pub.source) || Boolean(get().watching[p.identity]))
    if (want && pub.source === Track.Source.ScreenShare) watchedSid.set(p.identity, pub.trackSid)
    if (pub.isDesired !== want) pub.setSubscribed(want)
  }

  const syncParticipant = (p: RemoteParticipant) => {
    for (const pub of p.trackPublications.values()) syncSubscription(pub, p)
  }

  /** Закрыть просмотр в состоянии: трансляция кончилась, это уже другая или человек сам закрыл. */
  const forgetStream = (id: string) => {
    watchedSid.delete(id)
    set((s) => {
      if (!s.watching[id] && s.focus !== id) return {}
      const watching = { ...s.watching }
      delete watching[id]
      return { watching, focus: s.focus === id ? null : s.focus }
    })
  }

  /** Дорожку опубликовали — только что (fresh) или ещё до нашего входа: тогда звук не нужен. */
  const onPublished = (pub: RemoteTrackPublication, p: RemoteParticipant, fresh: boolean) => {
    const id = p.identity
    if (pub.source === Track.Source.ScreenShare) {
      const seen = watchedSid.get(id)
      if (seen && seen !== pub.trackSid) forgetStream(id)
      const auto = get().connection?.guildId === null
      set((s) => ({ live: { ...s.live, [id]: pub.trackName }, ...(auto && !s.watching[id] ? { watching: { ...s.watching, [id]: true as const } } : {}) }))
      if (fresh) sfx('streamStarted')
    } else if (pub.source === Track.Source.ScreenShareAudio) {
      // есть ли у трансляции звук, знаем по публикации: подписки на него может и не быть
      set((s) => ({ streamAudio: { ...s.streamAudio, [id]: true } }))
    }
    syncSubscription(pub, p)
  }

  const clear = () => {
    stopActivity?.(); stopActivity = null
    watchersBy.clear()
    audioRoot().replaceChildren()
    watchedSid.clear()
    gateLive = false
    captureStale = false
    stopPTT?.()
    stopPTT = null
    void mic?.destroy()
    mic = null
    void strip?.destroy()
    strip = null
    void dropSystemAudio()
    if (rttTimer) clearInterval(rttTimer)
    rttTimer = null
    ducker?.reset()
    set({ afk: false, connection: null, speaking: {}, streamAudio: {}, strip: null, cleanAudio: null, ownAudioRestricted: false, screens: {}, cameras: {}, watching: {}, viewers: {}, live: {}, focus: null, quality: {}, rtt: null, remote: {}, sharing: false, camera: false, micError: null, micHint: null, hintKind: null, transmitting: false, rnnoise: null, effects: {} })
  }

  const stopRemovedWait = () => {
    if (removedWait) clearTimeout(removedWait)
    removedWait = null
  }

  /** Отключили от голоса с сервера или голос ушёл на другое устройство: закрываем своё соединение. */
  const drop = () => {
    stopRemovedWait()
    const r = room
    room = null
    roomChannel = null
    if (r) {
      moving = true
      void r.disconnect().finally(() => {
        moving = false
      })
    }
    clear()
    sfx('leave')
  }

  const connect = async (join: VoiceJoin): Promise<void> => {
    const gen = ++connectGen
    stopRemovedWait()
    if (room) {
      moving = true
      await room.disconnect()
      moving = false
    }
    const prefs = get().prefs
    void autoVideoCodec.prepare(CAMERA_ENCODING)
    void autoVideoCodec.prepare(screenEncoding(prefs.screenShare))
    const r = new Room({
      adaptiveStream: true,
      dynacast: true,
      publishDefaults: autoVideoCodec.options(CAMERA_ENCODING),
      // звук собеседников через Web Audio (GainNode): громкость выше 100%, «не слышу» — громкость 0.
      // Контекст свой: из него же берём опору для вычитания голосов из трансляции.
      webAudioMix: { audioContext: audioContext() },
      audioCaptureDefaults: {
        ...(prefs.audioinput ? { deviceId: prefs.audioinput } : {}),
        noiseSuppression: prefs.noiseSuppression,
        echoCancellation: prefs.echoCancellation,
        autoGainControl: prefs.autoGainControl,
      },
      videoCaptureDefaults: prefs.videoinput ? { deviceId: prefs.videoinput } : {},
    })
    room = r
    roomChannel = join.channel_id
    joinedSession = join.voice_state?.session_id || null
    stopActivity?.()
    stopActivity = startVoiceActivity(() => ({ channelId: join.guild_id ? roomChannel : null, sessionId: joinedSession, speaking: get().transmitting }), active => useVoiceStore.subscribe(state => { if (state.transmitting) active() }))
    if (rttTimer) clearInterval(rttTimer)
    rttTimer = setInterval(() => {
      const v = room?.engine.client.rtt
      set({ rtt: v && v > 0 ? Math.round(v) : null })
    }, 2000)
    r.on(RoomEvent.ConnectionQualityChanged, (quality: ConnectionQuality, participant: Participant) => {
      set((s) => ({ quality: { ...s.quality, [participant.identity]: quality } }))
    })
    r.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      set((s) => ({ speaking: visibleSpeakers(speakers, s.transmitting) }))
    })
    const seedRemote = () => set({ remote: Object.fromEntries([...r.remoteParticipants.keys()].map((id) => [id, true as const])) })
    r.on(RoomEvent.Connected, seedRemote)
    r.on(RoomEvent.Reconnected, seedRemote)
    r.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
      sfx('userJoin')
      set((s) => ({ remote: { ...s.remote, [p.identity]: true } }))
      // новичок не слышал, кто что смотрит: рассказываем ему одному
      if (Object.keys(get().watching).length) sendWatching([p.identity])
    })
    r.on(RoomEvent.Reconnected, () => sendWatching())
    r.on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: RemoteParticipant, _kind?: unknown, topic?: string) => {
      if (topic !== WATCH_TOPIC || !participant) return
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload)) as { watching?: unknown }
        const list = Array.isArray(msg.watching) ? msg.watching.filter((x): x is string => typeof x === 'string').slice(0, 50) : []
        watchersBy.set(participant.identity, list)
        recomputeViewers()
      } catch {
        // не наше или битое сообщение — пропускаем
      }
    })
    r.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
      if (watchersBy.delete(p.identity)) recomputeViewers()
      sfx('userLeave')
      set((s) => {
        const remote = { ...s.remote }
        const streamAudio = { ...s.streamAudio }
        const live = { ...s.live }
        delete remote[p.identity]
        delete streamAudio[p.identity]
        delete live[p.identity]
        return { remote, streamAudio, live }
      })
    })
    r.on(RoomEvent.TrackPublished, (pub: RemoteTrackPublication, p: RemoteParticipant) => onPublished(pub, p, true))
    r.on(RoomEvent.TrackUnpublished, (pub: RemoteTrackPublication, p: RemoteParticipant) => {
      const id = p.identity
      if (pub.source === Track.Source.ScreenShare) {
        set((s) => {
          const live = { ...s.live }
          delete live[id]
          return { live }
        })
        /*
         * Человек ещё в комнате — значит, сам закончил показ, и просмотр закрываем. Если его
         * уже нет (ушёл или это мы переподключаемся — LiveKit сначала убирает участника, потом
         * снимает его публикации), просмотр не трогаем: та же трансляция вернётся с тем же sid.
         */
        if (r.remoteParticipants.get(id) === p) forgetStream(id)
      } else if (pub.source === Track.Source.ScreenShareAudio) {
        set((s) => {
          const streamAudio = { ...s.streamAudio }
          delete streamAudio[id]
          return { streamAudio }
        })
      }
    })
    // камера у собеседника выключена = трек замьючен, а не снят: убираем плитку и возвращаем при включении
    r.on(RoomEvent.TrackMuted, (pub, participant) => {
      if (pub.source !== Track.Source.Camera) return
      set((s) => {
        const cameras = { ...s.cameras }
        delete cameras[participant.identity]
        return participant.identity === (selfId ?? 'me') ? { camera: false, cameras } : { cameras }
      })
    })
    r.on(RoomEvent.TrackUnmuted, (pub, participant) => {
      if (pub.source !== Track.Source.Camera || !pub.track) return
      const track = pub.track
      set((s) => (participant.identity === (selfId ?? 'me') ? { camera: true, cameras: { ...s.cameras, [participant.identity]: track } } : { cameras: { ...s.cameras, [participant.identity]: track } }))
    })
    r.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        // элемент остаётся замьюченным (webAudioMix): звук идёт через граф Web Audio, громкость — participant.setVolume
        audioRoot().appendChild(track.attach())
        const { prefs, selfDeaf } = get()
        tapReference(track, participant, pub.source)
        if (pub.source === Track.Source.ScreenShareAudio) {
          applyRemoteVolume(participant, streamVolumeFor(prefs, selfDeaf, participant.identity) * duckLevel(prefs), Track.Source.ScreenShareAudio)
          set((s) => ({ streamAudio: { ...s.streamAudio, [participant.identity]: true } }))
        } else {
          applyRemoteVolume(participant, volumeFor(prefs, selfDeaf, participant.identity), Track.Source.Microphone)
        }
      } else if (pub.source === Track.Source.ScreenShare) {
        set((s) => ({ screens: { ...s.screens, [participant.identity]: track } }))
      } else if (pub.source === Track.Source.Camera && !pub.isMuted) {
        // выключенная камера у собеседника приходит замьюченной — плитку покажем по TrackUnmuted
        set((s) => ({ cameras: { ...s.cameras, [participant.identity]: track } }))
      }
    })
    r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      track.detach().forEach((el: HTMLMediaElement) => el.remove())
      dropReference(participant.identity, pub.source)
      // звук трансляции здесь не трогаем: отписка — это «закрыли просмотр», а звук у трансляции
      // остался; снимает его отмена публикации
      /*
       * Убираем только свою дорожку. Раньше конец демонстрации заодно стирал и камеру, и у того,
       * кто показывал экран с включённой камерой, после показа оставалась одна аватарка.
       */
      if (pub.source === Track.Source.ScreenShare) {
        set((s) => {
          const screens = { ...s.screens }
          delete screens[participant.identity]
          return { screens }
        })
      } else if (pub.source === Track.Source.Camera) {
        set((s) => {
          const cameras = { ...s.cameras }
          delete cameras[participant.identity]
          return { cameras }
        })
      }
    })
    r.on(RoomEvent.LocalTrackPublished, (pub: LocalTrackPublication) => {
      const track = pub.track
      if (!track) return
      if (pub.source === Track.Source.ScreenShare) set((s) => ({ sharing: true, screens: { ...s.screens, [selfId ?? 'me']: track }, live: { ...s.live, [selfId ?? 'me']: pub.trackName } }))
      // свой звук трансляции — чтобы на своей плитке было видно, что показываешь без звука
      if (pub.source === Track.Source.ScreenShareAudio) set((s) => ({ streamAudio: { ...s.streamAudio, [selfId ?? 'me']: true } }))
      if (pub.source === Track.Source.Camera) set((s) => ({ camera: true, cameras: { ...s.cameras, [selfId ?? 'me']: track } }))
    })
    r.on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => {
      if (pub.source === Track.Source.ScreenShare) {
        set((s) => {
          const self = selfId ?? 'me'
          const screens = { ...s.screens }
          const streamAudio = { ...s.streamAudio }
          const live = { ...s.live }
          delete screens[self]
          delete streamAudio[self]
          delete live[self]
          return { sharing: false, ownAudioRestricted: false, screens, streamAudio, live, focus: s.focus === self ? null : s.focus }
        })
        void guildApi.updateVoice({ self_stream: false }).catch(() => undefined)
      }
      if (pub.source === Track.Source.Camera) {
        set((s) => {
          const cameras = { ...s.cameras }
          delete cameras[selfId ?? 'me']
          return { camera: false, cameras }
        })
        void guildApi.updateVoice({ self_video: false }).catch(() => undefined)
      }
    })
    r.on(RoomEvent.Reconnecting, () => set((s) => (s.connection ? { connection: { ...s.connection, status: 'reconnecting' } } : {})))
    r.on(RoomEvent.Reconnected, () => set((s) => (s.connection ? { connection: { ...s.connection, status: 'connected' } } : {})))
    r.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      if (room !== r) return
      const cur = get().connection
      const from = roomChannel
      room = null
      roomChannel = null
      const wasMoving = moving
      clear()
      if (wasMoving) return
      if (reason === DisconnectReason.PARTICIPANT_REMOVED && cur) {
        /*
         * Из комнаты вывел сервер: мы сами переходим в другой канал, нас перенёс модератор или
         * отключил. Где мы теперь, сервер уже знает, и выходить из голоса нельзя: при переносе
         * это сняло бы новое состояние. Переходим сами — новую комнату откроет join, иначе ждём,
         * что скажет шлюз (см. removedWait).
         */
        if (cur.channelId !== from) {
          set({ connection: cur })
          return
        }
        set({ connection: { ...cur, status: 'reconnecting' } })
        removedWait = setTimeout(() => {
          removedWait = null
          clear()
          sfx('leave')
        }, REMOVED_WAIT_MS)
        return
      }
      sfx('leave')
      // в ту же комнату вошли с другого устройства: голос теперь там, снимать его нельзя
      if (reason === DisconnectReason.DUPLICATE_IDENTITY) return
      // сеть или SFU оборвали связь — снимаем состояние на сервере, чтобы не висеть «в канале»
      void guildApi.leaveVoice().catch(() => undefined)
    })
    set({ afk: Boolean(join.voice_state?.afk), connection: { guildId: join.guild_id, channelId: join.channel_id, status: 'connecting' }, error: null })
    try {
      await r.connect(join.endpoint, join.token, { autoSubscribe: false })
    } catch (err) {
      // пока подключались, нас повели дальше (ещё один перенос) или сервер вывел из этой комнаты
      // и ждём, что скажет шлюз: ошибка этой комнаты уже ничего не значит
      if (gen !== connectGen || removedWait) return
      room = null
      roomChannel = null
      clear()
      // текст LiveKit («could not establish signal connect») человеку ничего не говорит — он остаётся в консоли
      console.warn('голос не подключился:', err)
      set({ error: 'Не удалось подключиться к голосовому серверу. Проверьте интернет и попробуйте ещё раз' })
      void guildApi.leaveVoice().catch(() => undefined)
      return
    }
    if (gen !== connectGen || room !== r) return
    set({ connection: { guildId: join.guild_id, channelId: join.channel_id, status: 'connected' } })
    // то, что опубликовали до нашего входа, LiveKit событиями не присылает — разбираем сами
    for (const p of r.remoteParticipants.values()) {
      for (const pub of p.trackPublications.values()) onPublished(pub, p, false)
    }
    sfx('join')
    if (prefs.audiooutput) await r.switchActiveDevice('audiooutput', prefs.audiooutput).catch(() => undefined)
    if (!get().selfMute && !get().afk) {
      try {
        await enableMic(r)
        set({ micError: null, micHint: null, hintKind: null })
        await attachMic(r)
      } catch (err) {
        micFailed(err)
      }
    }
  }

  const prefs = loadPrefs()
  return {
    afk: false,
    connection: null,
    selfMute: prefs.startMuted,
    selfDeaf: false,
    sharing: false,
    camera: false,
    prefs,
    speaking: {},
    streamAudio: {},
    strip: null,
    cleanAudio: null,
    ownAudioRestricted: false,
    screens: {},
    cameras: {},
    watching: {},
    viewers: {},
    live: {},
    focus: null,
    quality: {},
    rtt: null,
    remote: {},
    error: null,
    micError: null,
    micHint: null,
    hintKind: null,
    transmitting: false,
    rnnoise: null,
    effects: {},

    setSelf: (userId) => {
      selfId = userId
    },

    join: async (guildId, channelId) => {
      const s = get()
      if (s.connection?.channelId === channelId && room) return
      stopRemovedWait()
      set({ error: null, connection: { guildId, channelId, status: 'connecting' } })
      let join: VoiceJoin
      try {
        join = await guildApi.joinVoice(channelId, { self_mute: s.selfMute, self_deaf: s.selfDeaf, session_id: gatewayClient()?.session ?? '' })
      } catch (err) {
        set({ error: describeError(err).message, connection: room && s.connection ? s.connection : null })
        throw err
      }
      await connect(join)
    },

    leave: async () => {
      stopRemovedWait()
      const r = room
      room = null
      roomChannel = null
      moving = true
      try {
        await r?.disconnect()
      } finally {
        moving = false
      }
      clear()
      sfx('leave')
      await guildApi.leaveVoice().catch(() => undefined)
    },

    toggleMute: async () => {
      if (get().afk) return
      const selfMute = !get().selfMute
      const selfDeaf = selfMute ? get().selfDeaf : false // включённый микрофон снимает и «не слышу»
      set((s) => ({ selfMute, selfDeaf, ...(selfMute ? { transmitting: false, ...(selfId ? { speaking: { ...s.speaking, [selfId]: false } } : {}) } : {}) }))
      // включили микрофон — «не слышу» снялось вместе с ним: рамки говорящих возвращаются сразу
      if (!selfDeaf) set((s) => ({ speaking: visibleSpeakers(room?.activeSpeakers ?? [], s.transmitting) }))
      // с выключенным микрофоном ворота больше не закроются сами — снимаем приглушение сразу
      if (selfMute) ducker?.reset()
      applyVolumes(room, get().prefs, selfDeaf)
      sfx(selfMute ? 'mute' : 'unmute')
      if (room) {
        try {
          if (selfMute) await room.localParticipant.setMicrophoneEnabled(false)
          else await enableMic(room)
          if (!selfMute) await attachMic(room)
        } catch (err) {
          micFailed(err)
        }
        await guildApi.updateVoice({ self_mute: selfMute, self_deaf: selfDeaf }).catch(() => undefined)
      }
    },

    toggleDeaf: async () => {
      if (get().afk) return
      const selfDeaf = !get().selfDeaf
      // «не слышу» выключает и микрофон, как в Discord, но, вернув звук, микрофон надо вернуть
      // в то состояние, в каком он был до этого: сам по себе он остался бы выключенным.
      if (selfDeaf) muteBeforeDeaf = get().selfMute
      const selfMute = selfDeaf ? true : muteBeforeDeaf
      // выключенный микрофон больше не пришлёт «ворота закрылись» — гасим огонёк сами,
      // иначе заглушившийся на полуслове остаётся с зелёной рамкой насовсем
      set((st) => ({ selfDeaf, selfMute, ...(selfMute ? { transmitting: false, ...(selfId ? { speaking: { ...st.speaking, [selfId]: false } } : {}) } : {}) }))
      // выключил звук — чужие рамки гаснут сразу; вернул — появляются у тех, кто говорит сейчас
      set((st) => ({ speaking: visibleSpeakers(room?.activeSpeakers ?? [], st.transmitting) }))
      if (selfMute) ducker?.reset()
      applyVolumes(room, get().prefs, selfDeaf)
      sfx(selfDeaf ? 'deafen' : 'undeafen')
      if (room) {
        try {
          if (selfMute) await room.localParticipant.setMicrophoneEnabled(false)
          else await enableMic(room)
          if (!selfMute) await attachMic(room)
        } catch {
          /* микрофон уже недоступен — сообщили при входе */
        }
        await guildApi.updateVoice({ self_deaf: selfDeaf, self_mute: selfMute }).catch(() => undefined)
      }
    },

    startScreen: async (chosen, title, testCodec) => {
      if (!room) return
      if (get().afk) return
      if (!canShareScreen) {
        set({ error: 'В этом браузере нет захвата экрана. Демонстрация работает в приложении и в браузерах на компьютере' })
        return
      }
      /*
       * Потолок качества считаем здесь, а не только в окне выбора: по кнопке «показать экран»
       * повторяется прошлый выбор, а он мог пережить и конец «Плюса», и снятие бустов.
       */
      const guildId = get().connection?.guildId ?? null
      const plus = useAuthStore.getState().user?.premium_type === 2
      const tier = guildId ? (useGuildStore.getState().guilds[guildId]?.premium_tier ?? 0) : 0
      const quality = clampShareQuality(chosen, shareCaps(plus, tier))
      const prefs = { ...get().prefs, screenShare: quality }
      set({ prefs })
      savePrefs(prefs)
      /*
       * 15 к/с — презентации и текст: держим разрешение, кадрами жертвуем.
       * 30 и 60 — игры и видео: наоборот, держим кадры. Без этого браузер на тяжёлой
       * картинке сохранял все пиксели и ронял поток до 14 к/с при выбранных 60.
       */
      const motion = quality.fps >= 30
      const share = (audio: ShareAudio) =>
        room!.localParticipant.setScreenShareEnabled(
          true,
          { audio, contentHint: motion ? 'motion' : 'detail', resolution: shareCapture(quality) },
          // один слой без simulcast: иначе зритель с адаптивным потоком получает слой под размер своей плитки (720p и ниже)
          {
            ...autoVideoCodec.options(screenEncoding(quality), testCodec),
            simulcast: false,
            degradationPreference: motion ? 'maintain-framerate' : 'maintain-resolution',
            screenShareEncoding: { maxBitrate: shareBitrate(quality), maxFramerate: quality.fps, priority: 'high' },
            // название окна уходит только в комнату: там его и так видно на самой трансляции
            ...(title ? { name: title.slice(0, 100) } : {}),
          },
        )
      /*
       * Windows умеет отдать звук компьютера без наших собственных процессов — тогда голосов
       * собеседников в захвате просто нет и вычитать нечего. Пробуем до окна выбора: если не
       * вышло, звук возьмём общесистемный, и человек ничего не заметит.
       */
      await dropSystemAudio()
      const clean = quality.muteAudio ? null : await startSystemAudio(audioContext())
      sysAudio = clean
      // звук из браузера: с «убирать голоса» и поддержкой — без звука самой вкладки Кличата
      const audio = shareAudio({ muted: quality.muteAudio || Boolean(clean), stripVoices: get().prefs.stripVoices, supported: restrictOwnAudioSupported, enabled: RESTRICT_OWN_AUDIO })
      try {
        try {
          await share(audio)
          // звук мог не поехать и без ошибки: в браузере его дают только по галочке
          // в окне выбора, и человек об этом не узнает, пока зрители не скажут
          set({ error: audio !== false && !room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio) ? noStreamAudioHint() : null })
        } catch (err) {
          // звук системы захватывается не везде (macOS, занятое устройство) — картинку из-за этого не теряем
          if (quality.muteAudio || clean || !isBusyDevice(err)) throw err
          await share(false)
          set({ error: noStreamAudioHint() })
        }
        await capScreenSize(quality)
        if (clean) {
          try {
            await publishSystemAudio(clean)
            // сыгранное показываем в канале: «пока тишина» на громкой игре сразу скажет, что захват молчит
            set({ cleanAudio: { bufferedMs: 0, starvedMs: 0, trimmedMs: 0, playedMs: 0 } })
            clean.onStats = (stats) => set({ cleanAudio: stats })
          } catch (err) {
            // картинка уже идёт — из-за звука её не роняем, просто говорим, что он не поехал
            await dropSystemAudio()
            set({ error: noStreamAudioHint() })
            if (err instanceof Error) console.warn('звук трансляции не опубликовался:', err.message)
          }
        }
        // браузер мог принять restrictOwnAudio, а мог молча не применить — смотрим, что вышло
        const restricted = typeof audio === 'object' && (await ownAudioRestricted())
        set({ ownAudioRestricted: restricted })
        sfx('screenStart')
        // при чистом захвате Windows и при restrictOwnAudio голосов в звуке нет — фильтр только
        // испортил бы звук игры; иначе вычитаем, как раньше
        if (!clean && !restricted) await attachStrip()
        await guildApi.updateVoice({ self_stream: true }).catch(() => undefined)
      } catch (err) {
        await dropSystemAudio()
        // закрытое окно выбора — не ошибка, человек просто передумал
        set({ error: isDenied(err) ? null : describeMediaError(err, 'screen', desktopPlatform) })
      }
    },

    stopScreen: async () => {
      if (!room) return
      void strip?.destroy()
      strip = null
      set({ strip: null, ownAudioRestricted: false })
      await dropSystemAudio()
      try {
        await room.localParticipant.setScreenShareEnabled(false)
        sfx('screenStop')
      } catch (err) {
        set({ error: err instanceof Error ? `Не удалось остановить демонстрацию: ${err.message}` : 'Не удалось остановить демонстрацию' })
      }
    },

    toggleScreen: async () => {
      if (get().sharing) await get().stopScreen()
      else await get().startScreen(get().prefs.screenShare)
    },

    toggleCamera: async () => {
      if (get().afk && !get().camera) return
      if (!room) return
      const next = !get().camera
      try {
        try {
          await room.localParticipant.setCameraEnabled(next, undefined, autoVideoCodec.options(CAMERA_ENCODING))
        } catch (err) {
          /*
           * То же, что с микрофоном: выбранная камера может остаться в списке и всё равно
           * не открыться. Пробуем ту, что система считает основной, и говорим об этом.
           */
          const p = get().prefs
          if (!next || (!isMissingDevice(err) && !isBusyDevice(err)) || !p.videoinput) throw err
          const capture = { ...(room.options.videoCaptureDefaults ?? {}) }
          delete capture.deviceId
          room.options.videoCaptureDefaults = capture
          await room.localParticipant.setCameraEnabled(true, undefined, autoVideoCodec.options(CAMERA_ENCODING))
          set({ micHint: 'Выбранная камера не открылась — включили ту, что система считает основной. Поменять можно в «Голос и видео»', hintKind: 'camera' })
        }
        // выключение камеры в LiveKit — мьют трека, а не снятие публикации: состояние ведём сами
        const track = next ? room.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack : undefined
        set((s) => {
          const cameras = { ...s.cameras }
          if (track) cameras[selfId ?? 'me'] = track
          else delete cameras[selfId ?? 'me']
          return { camera: next, cameras }
        })
        sfx(next ? 'cameraOn' : 'cameraOff')
        await guildApi.updateVoice({ self_video: next }).catch(() => undefined)
      } catch (err) {
        // камера падает по тем же причинам, что и микрофон, — подсказка ей нужна такая же
        set({ error: isDenied(err) ? null : describeMediaError(err, 'camera', desktopPlatform), micHint: mediaErrorHint(err, 'camera', desktopPlatform), hintKind: 'camera' })
      }
    },

    flipCamera: async () => {
      const track = room?.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack
      if (!room || !track || !get().camera) return
      /*
       * Размер и кадры — из умолчаний комнаты, а устройство, выбранное в настройках, — нет:
       * перевернуть камеру и значит уйти с него. В настройки выбор не пишем: там он остаётся
       * прежним, и следующий вход в канал начнётся с него.
       */
      const capture = { ...(room.options.videoCaptureDefaults ?? {}) }
      delete capture.deviceId
      delete capture.facingMode
      const was = track.mediaStreamTrack.getSettings().deviceId
      const next = facingModeFromLocalTrack(track).facingMode === 'environment' ? 'user' : 'environment'
      try {
        // перезапуск той же дорожки: публикация остаётся, собеседники видят смену без переподключения
        await track.restartTrack({ ...capture, facingMode: next })
        // facingMode — только пожелание: если камеры не сообщают, куда смотрят (бывает на планшетах
        // с Windows), браузер оставляет ту же. Тогда — следующая камера по списку
        if (track.mediaStreamTrack.getSettings().deviceId === was) {
          const cams = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput' && d.deviceId)
          const other = cams[(cams.findIndex((d) => d.deviceId === was) + 1) % cams.length]
          if (other && other.deviceId !== was) await track.restartTrack({ ...capture, deviceId: { exact: other.deviceId } })
        }
      } catch (err) {
        // другая камера не открылась (занята, запрещена) — возвращаем прежнюю, а не чёрную плитку
        await track.restartTrack().catch(() => undefined)
        set({ error: isDenied(err) ? null : describeMediaError(err, 'camera', desktopPlatform) })
      }
    },

    retryMic: async () => {
      if (!room) return
      set({ micError: null, micHint: null, hintKind: null })
      try {
        await enableMic(room)
        await attachMic(room)
        set({ selfMute: false })
        await guildApi.updateVoice({ self_mute: false }).catch(() => undefined)
      } catch (err) {
        micFailed(err)
      }
    },

    setUserVolume: (userId, volume) => {
      const v = Math.max(0, Math.min(2, volume))
      const prefs = { ...get().prefs, userVolumes: { ...get().prefs.userVolumes, [userId]: v } }
      set({ prefs })
      savePrefs(prefs)
      const p = room?.remoteParticipants.get(userId)
      if (p && !get().selfDeaf) applyRemoteVolume(p, v, Track.Source.Microphone)
    },

    setStreamVolume: (userId, volume) => {
      const v = Math.max(0, Math.min(2, volume))
      const prefs = { ...get().prefs, streamVolumes: { ...get().prefs.streamVolumes, [userId]: v } }
      set({ prefs })
      savePrefs(prefs)
      const p = room?.remoteParticipants.get(userId)
      if (p && !get().selfDeaf) applyRemoteVolume(p, v * duckLevel(prefs), Track.Source.ScreenShareAudio)
    },

    /*
     * Громкость здесь — усиление узла Web Audio (webAudioMix), а не element.volume: его iOS Safari
     * молча игнорирует, у элемента там работает только muted. Узел слушается везде, поэтому
     * та же кнопка выключает звук и на iPhone.
     */
    toggleStreamMute: (userId) => {
      const v = get().prefs.streamVolumes[userId] ?? 1
      if (v > 0) {
        volumeBeforeMute.set(userId, v)
        get().setStreamVolume(userId, 0)
      } else {
        get().setStreamVolume(userId, volumeBeforeMute.get(userId) ?? 1)
      }
    },

    watchStream: (userId) => {
      // свой экран смотреть не надо: дорожка и так здесь, просто разворачиваем её
      set((s) => ({ focus: userId, watching: userId === selfId || s.watching[userId] ? s.watching : { ...s.watching, [userId]: true } }))
      const p = room?.remoteParticipants.get(userId)
      if (p) syncParticipant(p)
    },

    stopWatching: (userId) => {
      forgetStream(userId)
      const p = room?.remoteParticipants.get(userId)
      if (p) syncParticipant(p)
    },

    setFocus: (userId) => set({ focus: userId }),

    setPrefs: async (patch) => {
      const prefs = { ...get().prefs, ...patch }
      savePrefs(prefs)
      set({ prefs })
      // режим активации, порог и RNNoise применяются на лету
      mic?.configure(micConfig(prefs))
      syncPTT()
      if ('outputVolume' in patch) applyVolumes(room, prefs, get().selfDeaf)
      if (patch.duckStream === false) ducker?.reset()
      if ('stripVoices' in patch) {
        if (patch.stripVoices) void attachStrip()
        else strip?.setEnabled(false)
      }
      if (!room) {
        if ('startMuted' in patch) set({ selfMute: prefs.startMuted })
        return
      }
      // устройства переключаются на лету
      const kinds: [keyof VoicePrefs, MediaDeviceKind][] = [['audioinput', 'audioinput'], ['audiooutput', 'audiooutput'], ['videoinput', 'videoinput']]
      for (const [key, kind] of kinds) {
        if (!(key in patch)) continue
        // «По умолчанию» — пустая строка: раньше посреди разговора с неё ничего не переключалось.
        // У Chrome для звука есть служебное устройство 'default' — на него и переключаемся
        const id = (prefs[key] as string) || (kind === 'videoinput' ? '' : 'default')
        if (id) await room.switchActiveDevice(kind, id).catch(() => undefined)
      }
      // обработку захвата меняем перезахватом дорожки — с выключенным микрофоном он не нужен,
      // новые значения подхватятся при включении
      const capture: (keyof VoicePrefs)[] = ['noiseSuppression', 'echoCancellation', 'autoGainControl']
      if (capture.some((k) => k in patch)) {
        if (get().selfMute || get().afk) captureStale = true
        else await restartMic(room)
      }
    },

    applyEvent: (t, d) => {
      if (t === 'VOICE_SERVER_UPDATE') {
        const join = d as VoiceJoin
        // модератор перенёс в другой канал: переходим в новую комнату по выданному токену —
        // самому подключиться туда может быть нельзя. Из старой connect выйдет сам
        if (get().connection && roomChannel !== join.channel_id) void connect(join)
        return
      }
      if (t === 'VOICE_STATE_UPDATE') {
        const vs = d as VoiceState
        const cur = get().connection
        if (!cur || vs.user_id !== selfId) return
        if (selfStateAction(cur, vs, joinedSession) === 'drop') { drop(); return }
        if (vs.channel_id === cur.channelId && vs.session_id === joinedSession && Boolean(vs.afk) !== get().afk) {
          const afk = Boolean(vs.afk)
          set({ afk, ...(afk ? { transmitting: false, speaking: {} } : {}) })
          ducker?.reset()
          applyVolumes(room, get().prefs, deafNow())
          if (room) for (const participant of room.remoteParticipants.values()) syncParticipant(participant)
          const currentRoom = room
          if (currentRoom) void (async () => {
            try {
              if (afk) {
                await currentRoom.localParticipant.setMicrophoneEnabled(false)
                if (get().camera) await get().toggleCamera()
                if (get().sharing) await get().stopScreen()
              } else if (!get().selfMute && !get().selfDeaf && room === currentRoom && !get().afk) {
                await enableMic(currentRoom)
                await attachMic(currentRoom)
              }
            } catch (err) { if (!get().afk) micFailed(err) }
          })()
        }
      }
      if (t === 'VOICE_CHANNEL_EFFECT_SEND') {
        const ev = d as VoiceChannelEffect
        const cur = get().connection
        if (!cur || ev.channel_id !== cur.channelId || !ev.sound_id) return
        markEffect(ev)
        // оглушённый (сам или сервером) звуков панели не слышит, как и голосов
        const serverDeaf = cur.guildId && selfId ? useGuildStore.getState().voiceStates[cur.guildId]?.[selfId]?.deaf : false
        if (get().afk || get().selfDeaf || serverDeaf) return
        const p = get().prefs
        const volume = Math.max(0, Math.min(1, ev.sound_volume ?? 1)) * p.soundboardVolume * p.outputVolume
        if (volume > 0) void playBoardSound(soundUrl(ev.sound_id), volume)
      }
    },

    sendSound: async (sound) => {
      const cur = get().connection
      if (!cur) return
      await soundboardApi.send(cur.channelId, sound)
    },
  }
})

// начали или бросили смотреть трансляцию — пересчитываем зрителей и говорим комнате
useGuildStore.subscribe((s, prev) => {
  const cur = useVoiceStore.getState().connection
  if (!cur?.guildId || !selfId) return
  if (s.voiceStates[cur.guildId]?.[selfId]?.deaf === prev.voiceStates[cur.guildId]?.[selfId]?.deaf) return
  useVoiceStore.setState((st) => ({ speaking: visibleSpeakers(room?.activeSpeakers ?? [], st.transmitting) }))
})

useVoiceStore.subscribe((s, prev) => {
  if (s.watching === prev.watching) return
  recomputeViewers()
  sendWatching()
})

/** Состояние соединения LiveKit для индикатора качества. */
export function connectionState(): ConnectionState | null {
  return room?.state ?? null
}
